import { useCallback, useEffect, useMemo, useState } from 'react';

import get from 'lodash/get';

import { createActionBusWithContextsFactory } from './controllers/actions';
import { type ActionDeclaration } from './controllers/actions/types';
import { createContextStore } from './controllers/context';
import {
	type Subscriber as ContextSubscriber,
	type NullableSurfacePlatformContext,
} from './controllers/context/types';
import { createSurfaceStore } from './controllers/store';
import { type SurfaceStore } from './controllers/store/types';
import { type SurfaceLoader } from './services/surface-loader/types';
import {
	type ContextDependencies,
	type CreateMutateAtModule,
	type EagerSurfaceInitializationOptions,
	type Mutation,
	type SurfaceInitializationOptions,
	type SurfaceSdk,
	type SurfaceSdkConfig,
	type SurfacesInitializationOptions,
	type SurfaceState,
	type SurfaceStateWithUpdateId,
} from './types';
import { buildCombinedKey } from './utils/buildCombinedKey';
import { getModulePathsInSurface } from './utils/getModulePathsInSurface';
import { setModuleStateAtPath } from './utils/setModuleState';
import { loadSurfaceExperience } from './utils/ufo';

const isEagerSurfaceInitialization = (
	surfaceInit: SurfaceInitializationOptions,
): surfaceInit is EagerSurfaceInitializationOptions =>
	!surfaceInit.load || surfaceInit.load === 'EAGER';

const eagerLoadSurfaces =
	(surfaceLoader: SurfaceLoader) =>
	async <S extends SurfacesInitializationOptions<any>>(
		surfaces: S,
		update: SurfaceStore<any>['update'],
		context?: Partial<NullableSurfacePlatformContext>,
	): Promise<void> => {
		Object.entries<SurfaceInitializationOptions>(surfaces).forEach(async ([name, surfaceInit]) => {
			if (!isEagerSurfaceInitialization(surfaceInit)) {
				return;
			}

			const { surfaceType, surfaceKey, fallback } = surfaceInit;
			const experienceMetaData = {
				metadata: {
					surfaceType,
					surfaceKey,
				},
			};

			const surfaceLoaderPromise = loadSurfaceExperience.start();

			const { surface, error } = await surfaceLoader({
				surfaceType,
				surfaceKey,
				contextResolver: () => {
					const { switcherMenuHasBeenOpened, ...rest } = context || {
						switcherMenuHasBeenOpened: true,
					};
					return rest;
				},
			});
			await surfaceLoaderPromise;
			if (surface) {
				// only record success metric in eager load as failure will be retried
				await loadSurfaceExperience.success(experienceMetaData);
			}

			update(name, {
				surface,
				isLoading: !surface,
				fallback,
				error,
			});
		});
	};

const isSurfaceStore = <SurfaceName extends string>(
	initializationOptions: SurfacesInitializationOptions<SurfaceName> | SurfaceStore<SurfaceName>,
): initializationOptions is SurfaceStore<SurfaceName> =>
	!!(initializationOptions as SurfaceStore<SurfaceName>).getCurrentSurfaceState;

const createMutateAtModuleFactory =
	<SurfaceName extends string>(
		getCurrentState: () => SurfaceState,
		update: SurfaceStore<SurfaceName>['update'],
	): CreateMutateAtModule =>
	(moduleType, moduleKey) =>
	(mutation: Mutation | null) => {
		const state = getCurrentState();

		update(state.name as SurfaceName, ({ surface }) => {
			const state = getCurrentState();
			const modulePaths = state.surface ? getModulePathsInSurface(state.surface) : new Map();

			if (!surface) {
				return {
					surface,
				};
			}

			const modulePath = modulePaths.get(buildCombinedKey(moduleType, moduleKey));

			if (!modulePath) {
				throw Error(
					`tried to update surface module with id of ${moduleKey} path but could not find that module`,
				);
			}

			const currentModuleConfiguration = get<{
				fields?: Record<string, any>;
			}>(state.surface, modulePath)?.fields;

			if (typeof mutation === 'function' && !currentModuleConfiguration) {
				throw Error(
					`tried to access currentState of surface module with id of ${moduleKey} path but could not find that module`,
				);
			}

			return {
				surface: setModuleStateAtPath(modulePath)(
					surface,
					typeof mutation === 'function' ? mutation(currentModuleConfiguration) : mutation,
				),
			};
		});
	};

const isContextReady =
	(contextDependencies: ContextDependencies = []) =>
	<T extends Record<string, any>>(subscriberContext: T): boolean =>
		contextDependencies.every((dep) => {
			if (typeof dep === 'string') {
				return Object.keys(subscriberContext).includes(dep);
			}

			if (dep?.match) {
				return !!subscriberContext?.[dep.field]?.match(dep.match);
			}

			if (dep?.required) {
				return !!(subscriberContext?.[dep.field] ?? false);
			}

			return true;
		});

const transformSurfacesToSubscribers =
	(surfaceLoader: SurfaceLoader, update: (...args: any) => void) =>
	<SurfaceName extends string>(
		surfaces: SurfacesInitializationOptions<SurfaceName>,
	): ContextSubscriber[] =>
		Object.entries<SurfaceInitializationOptions>(surfaces)
			.filter(
				(surface): surface is [SurfaceName, EagerSurfaceInitializationOptions] =>
					surface[1].load === 'LAZY' || surface[1].load === undefined,
			)
			.map(([surfaceName, { contextDependencies, ...surface }]) => [
				({ subscriberContext }) => {
					if (!isContextReady(contextDependencies)(subscriberContext)) {
						return;
					}

					eagerLoadSurfaces(surfaceLoader)({ [surfaceName]: surface }, update, subscriberContext);
				},
				contextDependencies?.map((dep) => (typeof dep === 'string' ? dep : dep.field)),
				{ updateOnSubscribe: true },
			]);

export const createSurfaceSdk = <
	SurfaceName extends string,
	Actions extends ActionDeclaration = ActionDeclaration,
>({
	surfaces,
	surfaceLoader,
	sideEffects = [],
	context = {},
	contextMiddleware,
}: SurfaceSdkConfig<SurfaceName, Actions>): SurfaceSdk<SurfaceName, Actions> => {
	const contextStore = createContextStore({
		initialState: context,
		middleware: contextMiddleware,
	});

	const store = isSurfaceStore(surfaces)
		? surfaces
		: (() => {
				const _store = createSurfaceStore(surfaces);

				const surfaceToSubs = transformSurfacesToSubscribers(surfaceLoader, _store.update);

				contextStore.subscribe(...surfaceToSubs(surfaces));

				return _store;
			})();

	const useSurface = (
		surfaceName: Parameters<typeof store.getCurrentSurfaceState>[0],
		analyticsSideEffect?: NonNullable<SurfaceSdkConfig<any, Actions>['sideEffects']>[0],
	) => {
		const [state, setState] = useState<SurfaceStateWithUpdateId>(
			store.getCurrentSurfaceState(surfaceName),
		);

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const createMutateAtModule = useCallback(
			createMutateAtModuleFactory(() => store.getCurrentSurfaceState(surfaceName), store.update),
			[],
		);

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const createActionBusWithContexts = useMemo(
			() =>
				createActionBusWithContextsFactory<Actions>({
					sideEffects: analyticsSideEffect ? [...sideEffects, analyticsSideEffect] : sideEffects,
				}),
			[analyticsSideEffect],
		);

		const lazyLoadSurface = useCallback(
			async (surfaceType: string, surfaceKey: string) => {
				try {
					store.getCurrentSurfaceStateFromKeys(surfaceType, surfaceKey);
					return;
				} catch {
					const experienceMetaData = {
						metadata: {
							surfaceType,
							surfaceKey,
						},
					};
					const surfaceLoaderPromise = loadSurfaceExperience.start();
					const { surface, error } = await surfaceLoader({
						surfaceType,
						surfaceKey,
						contextResolver: () => {
							const context = contextStore.getCurrentContext();
							const { switcherMenuHasBeenOpened, ...rest } = context || {
								switcherMenuHasBeenOpened: true,
							};
							return rest;
						},
					});
					await surfaceLoaderPromise;
					if (surface) {
						await loadSurfaceExperience.success(experienceMetaData);
					} else {
						await loadSurfaceExperience.failure(experienceMetaData);
					}

					store.update(surfaceName, {
						error,
						surface,
						isLoading: false,
					});
				}
			},

			[surfaceName],
		);

		useEffect(() => {
			const unsubscribe = store.subscribe(setState, [surfaceName], {
				updateOnSubscribe: true,
			});

			const surfaceInit = store.getSurfaceInitializationOptions(surfaceName);
			const currentState = store.getCurrentSurfaceState(surfaceName);

			if (
				(surfaceInit?.load === 'LAZY' && surfaceInit?.surfaceType && surfaceInit?.surfaceKey) ||
				(surfaceInit &&
					isEagerSurfaceInitialization(surfaceInit) &&
					currentState?.error instanceof Error) // Try to reload if eager load has previously failed
			) {
				lazyLoadSurface(surfaceInit.surfaceType, surfaceInit.surfaceKey);
			}

			return unsubscribe;
		}, [surfaceName, lazyLoadSurface]);

		return {
			createMutateAtModule,
			createActionBusAtModule: createActionBusWithContexts({
				client: contextStore.getCurrentContext(),
				surfaceName: state.surface?.type ?? surfaceName, // Use surface type from server, otherwise use the surface name given.
			}),
			context: contextStore.getCurrentContext(),
			...state,
		};
	};

	return {
		useSurface,
		updateContext: contextStore.update,
		store,
	};
};

// Public exports

export type { SurfaceSdkConfig, Fallback } from './types';

export type {
	HeadingLevel,
	SurfaceModule,
	SurfaceModuleProps,
	SurfaceModuleOnAction,
	SurfaceModuleDisptachAction,
} from './common/types';

export type { NullableSurfacePlatformContext } from './controllers/context/types';

export { createSurfaceRenderer } from './controllers/surfaceRenderer';
export { createSurfaceLoader } from './services/surface-loader';

export type { Contexts } from './controllers/actions/types';
